/**
 * Unified Image Upload Service
 * Handles all image uploads and returns public URLs
 */

export type ImageCategory = 'profile' | 'website' | 'complaint_support' | 'general';

export interface ImageUploadResponse {
  success: boolean;
  url: string;
  filename: string;
  size: number;
  type: string;
  message?: string;
}

export interface ImageUploadError {
  message: string;
  code?: string;
}

class ImageUploadService {
  private baseUrl: string;

  constructor() {
    // Get base URL from environment or use current origin
    // In production, this should be set to the public URL
    // In development, it will use localhost
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 
                   import.meta.env.VITE_PUBLIC_URL || 
                   window.location.origin;
  }

  /**
   * Get public URL for an image
   * Handles both full URLs and relative paths
   */
  getPublicUrl(url: string): string {
    if (!url) return '';
    
    // If already a full URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If relative path, make it absolute
    if (url.startsWith('/')) {
      return `${this.baseUrl}${url}`;
    }
    
    // Otherwise, prepend base URL
    return `${this.baseUrl}/${url}`;
  }

  /**
   * Upload a single image
   */
  async uploadImage(
    file: File,
    category: ImageCategory = 'general',
    isPublic: boolean = true
  ): Promise<ImageUploadResponse> {
    // Validate file
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error(`File size must be less than ${maxSize / (1024 * 1024)}MB`);
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('is_public', isPublic.toString());

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${this.baseUrl}/api/v1/image-upload/${category}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Upload failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('Upload response data:', data);
      
      // Extract URL from response - check multiple possible locations
      let imageUrl = data.data?.url || data.data?.file_path || data.url || data.data?.file_url;
      
      console.log('Extracted imageUrl:', imageUrl);
      
      // If URL is relative, make it absolute
      if (imageUrl && !imageUrl.startsWith('http')) {
        const originalUrl = imageUrl;
        imageUrl = this.getPublicUrl(imageUrl);
        console.log('Converted URL from', originalUrl, 'to', imageUrl);
      }

      if (!imageUrl) {
        console.error('No URL found in response. Full response:', data);
        throw new Error('No URL returned from server. Check console for details.');
      }
      
      console.log('Final image URL:', imageUrl);

      return {
        success: true,
        url: imageUrl,
        filename: data.data?.filename || file.name,
        size: data.data?.file_size || file.size,
        type: data.data?.mime_type || file.type,
        message: data.message || 'Image uploaded successfully'
      };
    } catch (error) {
      console.error('Image upload error:', error);
      throw error instanceof Error ? error : new Error('Failed to upload image');
    }
  }

  /**
   * Upload multiple images
   */
  async uploadMultipleImages(
    files: File[],
    category: ImageCategory = 'general',
    isPublic: boolean = true
  ): Promise<ImageUploadResponse[]> {
    const uploadPromises = files.map(async (file, index) => {
      try {
        return await this.uploadImage(file, category, isPublic);
      } catch (error) {
        console.error(`Error uploading file ${index + 1} (${file.name}):`, error);
        return {
          success: false,
          url: '',
          filename: file.name,
          size: file.size,
          type: file.type,
          message: error instanceof Error ? error.message : 'Upload failed'
        } as ImageUploadResponse;
      }
    });

    try {
      const results = await Promise.all(uploadPromises);
      console.log('All upload results:', results);
      return results;
    } catch (error) {
      console.error('Multiple image upload error:', error);
      throw error;
    }
  }

  /**
   * Validate image file
   */
  validateImage(file: File): { valid: boolean; error?: string } {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return {
        valid: false,
        error: 'File must be an image (JPEG, PNG, GIF, WebP, etc.)'
      };
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size must be less than ${maxSize / (1024 * 1024)}MB`
      };
    }

    return { valid: true };
  }
}

// Export singleton instance
export const imageUploadService = new ImageUploadService();

